import { useEffect, useState } from "react";
import CalendarDisponibility from "./CalendarDisponibility";
import { useDataUser } from "../../context/dataUserContext";
import GestionCitas from "./GestionCitas";
import ConfiguracionEpaycoDoctor from "./ConfiguracionEpaycoDoctor";
import ConfiguracionPreciosCitas from "./ConfiguracionPreciosCitas";
import apiClient from "../../utils/axiosPetition";
import { useNavigate, useLocation } from "react-router-dom";
import { alertaGeneral } from "../../utils/alertas";
import Swal from "sweetalert2";
import "../../styles/Alertas.css";

const ConfiguracionCitasDoctor = () => {
    const { doctorId } = useDataUser();
    const [doctorEstado, setDoctorEstado] = useState(null);
    const [doctorTipoPerfil, setDoctorTipoPerfil] = useState(null);
    const [loading, setLoading] = useState(true);
    const [highlightHorarios, setHighlightHorarios] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    const isAprobado = doctorEstado === 'aprobado';
    const isPendienteEpayco = doctorEstado === 'pendiente_epayco';
    const hasAccess = isAprobado || isPendienteEpayco;

    useEffect(() => {
        if (!doctorId) {
            alertaGeneral({
                titulo: "Error",
                messageHtml: "No se encontró información del doctor. Por favor, inicia sesión nuevamente.",
                isError: true
            }).then(() => {
                navigate('/login');
            });
            return;
        }

        apiClient.get(`/api/afiliado/getState/${doctorId}`)
            .then((res) => {
                setLoading(false);
                if (!res.data) {
                    alertaGeneral({
                        titulo: "Error",
                        messageHtml: "No se pudo obtener el estado del doctor",
                        isError: true
                    });
                    return;
                }

                const { state, tipoPerfil } = res.data;
                setDoctorEstado(state);
                setDoctorTipoPerfil(tipoPerfil);

                if (tipoPerfil === 'experto') {
                    Swal.fire({
                        title: 'Configuración no disponible',
                        html: `
                            <p class="swal-perfil-mensaje">Esta sección es para profesionales que prestan servicios clínicos.</p>
                            <p class="swal-perfil-sub">Si deseas atender pacientes, completa el perfil clínico completo desde tu perfil.</p>
                        `,
                        icon: 'info',
                        iconColor: '#1f94c2',
                        confirmButtonText: 'Activar prestación de servicios',
                        confirmButtonColor: '#205087',
                        customClass: {
                            popup: 'swal-perfil-popup',
                            title: 'swal-perfil-title',
                            htmlContainer: 'swal-perfil-html',
                            confirmButton: 'swal-perfil-button'
                        },
                        width: '420px',
                        padding: '1.5rem 1.75rem',
                        showCloseButton: true,
                        allowOutsideClick: true,
                        allowEscapeKey: true
                    }).then((result) => {
                        if (result.isConfirmed) {
                            navigate('/doctor/perfil', { state: { activarPrestacionServicios: true } });
                        }
                    });
                    return;
                }

                switch (state) {
                    case 'aprobado':
                    case 'pendiente_epayco':
                        break;
                    case 'no_aprobado':
                        Swal.fire({
                            title: 'Configuración de citas no disponible',
                            html: `
                                <p class="swal-perfil-mensaje">Para configurar horarios y citas primero debes <strong>completar tu perfil profesional</strong> y habilitar la opción de atender pacientes.</p>
                                <p class="swal-perfil-sub">Dirígete a tu perfil para activar esta función.</p>
                            `,
                            icon: 'info',
                            iconColor: '#1f94c2',
                            confirmButtonText: 'Ir a mi perfil',
                            confirmButtonColor: '#205087',
                            customClass: {
                                popup: 'swal-perfil-popup',
                                title: 'swal-perfil-title',
                                htmlContainer: 'swal-perfil-html',
                                confirmButton: 'swal-perfil-button'
                            },
                            width: '420px',
                            padding: '1.5rem 1.75rem',
                            showCloseButton: true,
                            allowOutsideClick: true,
                            allowEscapeKey: true
                        }).then((result) => {
                            if (result.isConfirmed) {
                                navigate('/doctor/perfil');
                            }
                        });
                        break;
                    case 'en_revision':
                        alertaGeneral({
                            titulo: 'Tu solicitud de prestación de servicios está en revisión',
                            isWarning: true
                        });
                        navigate('/doctor/perfil');
                        break;
                    case 'rechazado':
                        alertaGeneral({
                            titulo: 'Tu solicitud de prestación de servicios ha sido rechazada',
                            messageHtml: 'Reenvía los documentos nuevamente',
                            isWarning: true
                        });
                        navigate('/doctor/perfil');
                        break;
                    default:
                        alertaGeneral({
                            titulo: 'Estado desconocido',
                            messageHtml: `Estado del doctor: ${state}. Contacta al administrador.`,
                            isWarning: true
                        });
                }
            })
            .catch(error => {
                console.error('Error en la llamada al backend:', error);
                setLoading(false);
                alertaGeneral({
                    titulo: "Error",
                    messageHtml: error.response?.data?.message || "Ocurrió un error inesperado",
                    isError: true
                });
            });
    }, [doctorId, navigate]);

    useEffect(() => {
        if (!hasAccess || !location.state?.scrollToDisponibilidad) return;
        const scrollAndHighlight = () => {
            const el = document.getElementById('seccion-horarios-disponibilidad');
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                setHighlightHorarios(true);
                setTimeout(() => setHighlightHorarios(false), 2800);
            }
            navigate(location.pathname, { replace: true, state: {} });
        };
        const t = setTimeout(scrollAndHighlight, 400);
        return () => clearTimeout(t);
    }, [hasAccess, location.state?.scrollToDisponibilidad, location.pathname, navigate]);

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Verificando autorización...</p>
                </div>
            </div>
        );
    }

    if (!hasAccess) {
        return null;
    }

    return (
        <div className="space-y-9">
            {isPendienteEpayco && (
                <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
                    <strong>Paso actual:</strong> configura tu cuenta ePayco. Cuando el equipo confirme tu registro, podrás definir tarifas y horarios de atención.
                </div>
            )}

            {isPendienteEpayco && (
                <ConfiguracionEpaycoDoctor
                    doctorId={doctorId}
                    doctorEstado={doctorEstado}
                    readOnly={false}
                />
            )}

            {isAprobado && (
                <>
                    <GestionCitas doctorId={doctorId} />

                    <CalendarDisponibility doctorId={doctorId} highlightSection={highlightHorarios} />

                    <ConfiguracionPreciosCitas doctorId={doctorId} doctorEstado={doctorEstado} />
                </>
            )}
        </div>
    );
};

export default ConfiguracionCitasDoctor;
